/**
 * A custom element that manages the account login actions.
 *
 * @extends {HTMLElement}
 */
class AccountLoginActions extends HTMLElement {
  shopLoginButton = null;

  connectedCallback() {
    this.shopLoginButton = this.querySelector('shop-login-button');

    // -----------------------
    // New Shopify login button
    // -----------------------
    if (this.shopLoginButton) {
      this.shopLoginButton.setAttribute('full-width', 'true');
      this.shopLoginButton.setAttribute('persist-after-sign-in', 'true');
      this.shopLoginButton.setAttribute('analytics-context', 'loginWithShopSelfServe');
      this.shopLoginButton.setAttribute('flow-version', 'account-actions-popover');
      this.shopLoginButton.setAttribute('return-uri', window.location.href);

      this.shopLoginButton.addEventListener('completed', () => {
        window.location.reload();
      });
    }

    // -----------------------
    // Classic login form AJAX
    // -----------------------
    this.initClassicLoginForm();

    // -----------------------
    // Forgot password toggle
    // -----------------------
    this.initForgotPasswordToggle();
  }

  initClassicLoginForm() {
    const loginForm = this.querySelector('form[action="/account/login"]');
    if (!loginForm) return;

    loginForm.addEventListener('submit', async (event) => {
      event.preventDefault();

      const emailInput = loginForm.querySelector('input[name="customer[email]"]');
      const passwordInput = loginForm.querySelector('input[name="customer[password]"]');

      // Remove previous errors
      loginForm.querySelectorAll('.form-error-message').forEach(el => el.remove());

      const errorMessage = document.createElement('div');
      errorMessage.classList.add('form-error-message');
      Object.assign(errorMessage.style, {
        color: '#d00',
        fontSize: '0.875rem',
        marginTop: '4px'
      });

      // Frontend validation
      if (!emailInput.value.trim() || !passwordInput.value.trim()) {
        errorMessage.textContent = 'Please fill in both fields.';
        passwordInput.insertAdjacentElement('afterend', errorMessage);
        return;
      }

      // Disable submit button while processing
      const submitBtn = loginForm.querySelector('button[type="submit"], button');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Signing in...';
      }

      const formData = new FormData(loginForm);

      try {
        const response = await fetch('/account/login', {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        const html = await response.text();

        if (html.includes('form__message') && html.includes('Error')) {
          errorMessage.textContent = 'Incorrect email or password. Please try again.';
          passwordInput.insertAdjacentElement('afterend', errorMessage);
        } else if (response.redirected || html.includes('/account')) {
          // Login successful → redirect
          window.location.href = '/account';
        } else {
          errorMessage.textContent = 'Something went wrong. Please try again.';
          passwordInput.insertAdjacentElement('afterend', errorMessage);
        }
      } catch (err) {
        console.error('Login request failed:', err);
        errorMessage.textContent = 'Network error. Please check your connection.';
        passwordInput.insertAdjacentElement('afterend', errorMessage);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'SIGN IN';
        }
      }
    });
  }

  initForgotPasswordToggle() {
    const recoverBtn = this.querySelector('#recover-button');
    const recoverForm = this.querySelector('.customer-forgot-button');
    const loginForm = this.querySelector('form[action="/account/login"]');
    const cancelRecover = recoverForm ? recoverForm.querySelector('a[href="#login"]') : null;

    if (recoverBtn && recoverForm) {
      recoverBtn.addEventListener('click', () => {
        recoverForm.style.display = 'block';
        if (loginForm) loginForm.style.display = 'none';

        const recoverEmail = recoverForm.querySelector('input[name="email"]');
        if (recoverEmail) recoverEmail.focus();
      });
    }

    if (cancelRecover && recoverForm) {
      cancelRecover.addEventListener('click', (e) => {
        e.preventDefault();
        recoverForm.style.display = 'none';
        if (loginForm) loginForm.style.display = 'block';
      });
    }
  }
}

// -----------------------
// Define element safely
// -----------------------
if (!customElements.get('account-login-actions')) {
  customElements.define('account-login-actions', AccountLoginActions);
}
